import { Box, Stack } from "@mui/material";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ZoomContext } from "../../zoom/zoom-context";
import { useHostControls } from "../hooks/useHostControls";
import { useLobbyChannel } from "../hooks/useLobbyChannel";
import { useScreenShare } from "../hooks/useScreenShare";
import { useActiveSpeakers } from "../hooks/use-active-speakers";
import { useTelehealthSession } from "../hooks/useTelehealthSession";
import { VIDEO_BG } from "../styles";
import {
  HOST_ASK_UNMUTE_MESSAGE,
  ZOOM_CLIENT_INIT_MESSAGE,
} from "../telehealth-v2-constant";
import { RoomParams } from "../types";
import { buildGuestUrl } from "../utils/url";
import AdmitKnockModal from "./AdmitKnockModal";
import ConnectionStatus from "./ConnectionStatus";
import ControlBar from "./ControlBar";
import HostActionBar from "./HostActionBar";
import InitializingScreen from "./InitializingScreen";
import Lobby from "./Lobby";
import ParticipantsPanel from "./ParticipantsPanel";
import PreflightScreen from "./PreflightScreen";
import RejoinPrompt from "./RejoinPrompt";
import ScreenShareView from "./ScreenShareView";
import VideoStage from "./VideoStage";

interface Props {
  params: RoomParams;
  isPublicView: boolean;
  onExit?: () => void;
  /** Mini-mode is owned by the parent overlay so the wrapper can switch
   *  between full-screen and floating-panel layout without remounting the
   *  Zoom session. */
  isMiniMode?: boolean;
  onEnterMiniMode?: () => void;
  onExitMiniMode?: () => void;
  /** Host action bar callbacks. When omitted the bar is hidden. */
  patientName?: string;
  providerName?: string;
  onCancelAppointment?: (reason: string) => Promise<void> | void;
  onNoShow?: () => Promise<void> | void;
  onStartEncounter?: () => Promise<void> | void;
}

export default function TelehealthRoom({
  params,
  isPublicView,
  onExit,
  isMiniMode = false,
  onEnterMiniMode,
  patientName,
  providerName,
  onCancelAppointment,
  onNoShow,
  onStartEncounter,
}: Readonly<Props>) {
  const session = useTelehealthSession(params, isPublicView);
  const speakingIds = useActiveSpeakers();
  const lobby = useLobbyChannel({
    role: params.role,
    selfId: session.selfId,
    phase: session.phase,
    setPhase: session.setPhase,
    onDuplicateHost: async () => {
      // Another provider is already hosting this room — leave cleanly so
      // we don't show two host-side UIs to participants, then surface the
      // reason and exit. The `onExit` callback closes the modal/overlay.
      await session.leave();
      showSnackbar({
        type: "error",
        message:
          "Another provider is already in this meeting. You can rejoin once they leave.",
      });
      onExit?.();
    },
    onHostLeft: async ({ final }) => {
      // Final = host clicked End. Tear down completely and close the
      // overlay/tab — the meeting is over.
      // Final = false (default for pagehide / user-removed backstop) =
      // host's tab is just unloading, likely a refresh. Disconnect from
      // the SDK session but leave the overlay mounted so the guest can
      // rejoin once the host's tab finishes reloading and they re-enter.
      if (final) {
        await session.leave();
        showSnackbar({
          type: "info",
          message: "The host has ended the meeting.",
        });
        onExit?.();
        return;
      }
      // Non-final: leave the SDK session but stay in the room. Setting
      // phase to "disconnected" surfaces the RejoinPrompt (Connection
      // lost) which carries the Rejoin button — clicking it puts the
      // guest back into the lobby and the host (when they finish
      // refreshing) sees their knock immediately.
      await session.leave();
      session.setPhase("disconnected");
      showSnackbar({
        type: "info",
        message: "The host disconnected — you can rejoin when they return.",
      });
    },
  });
  const host = useHostControls(session.isHost);
  const share = useScreenShare();
  const showSnackbar = useSnackbar();
  const zmClient = useContext(ZoomContext);
  // const { mutateAsync: inviteAppointment } = useInviteInstantAppointment();

  const [panelOpen, setPanelOpen] = useState(false);

  // Notify when someone leaves the meeting. Skip never-admitted knockers
  // (they were denied/kicked from the lobby — host already knows).
  const admittedIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    admittedIdsRef.current = new Set([
      ...lobby.admittedIds,
      ...(session.selfId !== undefined ? [session.selfId] : []),
    ]);
    // Anyone we have ever seen as an in-meeting participant counts as
    // "was in the meeting" for the purpose of leave notifications.
    session.participants.forEach((p) => admittedIdsRef.current.add(p.userId));
  }, [lobby.admittedIds, session.participants, session.selfId]);

  useEffect(() => {
    if (!zmClient) return;
    const onRemoved = (payload: any) => {
      const list = Array.isArray(payload) ? payload : [payload].filter(Boolean);
      list.forEach((u: any) => {
        const userId = Number(u?.userId);
        const name = u?.displayName ?? "A participant";
        if (!Number.isFinite(userId)) return;
        if (userId === session.selfId) return;
        if (!admittedIdsRef.current.has(userId)) return;
        showSnackbar({ type: "info", message: `${name} left the meeting` });
      });
    };
    zmClient.on("user-removed", onRemoved);
    return () => zmClient.off("user-removed", onRemoved);
  }, [zmClient, session.selfId, showSnackbar]);

  // Zoom never unmutes a participant on the host's say-so; it delivers this
  // consent prompt instead, so the mic only opens by the user's own action.
  useEffect(() => {
    if (!zmClient) return;
    const onAskUnmute = () => {
      showSnackbar({ type: "info", message: HOST_ASK_UNMUTE_MESSAGE });
    };
    zmClient.on("host-ask-unmute-audio", onAskUnmute);
    return () => zmClient.off("host-ask-unmute-audio", onAskUnmute);
  }, [zmClient, showSnackbar]);

  // When a guest is admitted (phase: lobby → active) the participants list
  // and video states already exist in the SDK from before they entered, but
  // VideoStage is mounting fresh. Schedule a few refreshes so the canvas
  // refs land before renderVideo is called and any host video that was
  // already on appears immediately for the guest.
  const prevPhaseRef = useRef(session.phase);
  useEffect(() => {
    if (prevPhaseRef.current === "lobby" && session.phase === "active") {
      session.refresh();
      window.setTimeout(session.refresh, 250);
      window.setTimeout(session.refresh, 1000);
    }
    prevPhaseRef.current = session.phase;
  }, [session.phase, session]);

  // Auto-open the participants panel the first time a guest knocks so the
  // host can't miss the admit prompt while focused on the video.
  const lastKnockCountRef = useRef(0);
  useEffect(() => {
    if (
      session.isHost &&
      lobby.knocks.length > lastKnockCountRef.current &&
      !isMiniMode
    ) {
      setPanelOpen(true);
    }
    lastKnockCountRef.current = lobby.knocks.length;
  }, [lobby.knocks.length, session.isHost, isMiniMode]);

  // Each click of "Copy link" mints a fresh URL with a unique Guest N name
  // so the host can paste a different link to each invitee. The number is
  // chosen by combining (a) the highest Guest N already in the meeting and
  // (b) a click counter, so even rapid-fire copies before any guest joins
  // still produce distinct names.
  const lastMintedRef = useRef(0);
  const buildInviteUrl = useCallback((): string | undefined => {
    if (!session.isHost || !params.room) return undefined;
    let highest = 0;
    const re = /^Guest\s+(\d+)$/i;
    for (const p of session.participants) {
      const m = re.exec(p?.displayName ?? "");
      if (m) {
        const n = parseInt(m[1], 10);
        if (Number.isFinite(n) && n > highest) highest = n;
      }
    }
    const next = Math.max(highest, lastMintedRef.current) + 1;
    lastMintedRef.current = next;
    // Open/public guest link — intentionally omits patientId so the join
    // route stays unauthenticated. The patient-specific link (sent via
    // email invite) carries patientId and triggers the DOB challenge on
    // the public route before the meeting renders.
    return (
      window.location.origin +
      buildGuestUrl({
        room: params.room,
        displayName: `Guest ${next}`,
        appointmentId: params.appointmentId,
        startTime: params.startTime,
      })
    );
  }, [
    session.isHost,
    session.participants,
    params.room,
    params.appointmentId,
    params.startTime,
  ]);

  // Bulk-invite handler driven by the ParticipantsPanel invite section.
  // Each patient gets the patient-specific URL (carries patientId so the
  // public guest route runs the DOB challenge); each provider gets the
  // open public URL (no patientId, anyone can join).
  // const handleInvite = useCallback(() => { }, [])
  // async ({ patients, providers }: InviteRecipients) => {
  //   return { patients: [], providers: [] }
  // if (!params.room || !params.appointmentId) {
  //   showSnackbar({
  //     type: "error",
  //     message: "Cannot send invites — appointment context missing.",
  //   });
  //   return;
  // }
  // The API needs a providerId for the invite; fall back to the host's
  // patientId field if no caller-specified providerId is in scope.
  // const providerId = params.patientId ?? "";
  // const buildPatientUrl = (patientId: string) =>
  //   window.location.origin +
  //   buildGuestUrl({
  //     room: params.room,
  //     displayName: patientName ?? "Patient",
  //     appointmentId: params.appointmentId,
  //     patientId,
  //     startTime: params.startTime,
  //   });
  // const openUrl =
  //   window.location.origin +
  //   buildGuestUrl({
  //     room: params.room,
  //     displayName: "Guest",
  //     appointmentId: params.appointmentId,
  //     startTime: params.startTime,
  //   });

  // console.log("val here check", openUrl, buildPatientUrl, providerId,);

  // const tasks: Promise<unknown>[] = [];
  // for (const p of patients) {
  //   tasks.push(
  //     inviteAppointment({
  //       appointmentId: params.appointmentId,
  //       patientId: p.value,
  //       providerId,
  //       appointmentUrl: buildPatientUrl(p.value),
  //       xTENANTID,
  //     })
  //   );
  // }
  // for (const pr of providers) {
  //   tasks.push(
  //     inviteAppointment({
  //       appointmentId: params.appointmentId,
  //       // Backend resolves the recipient via patientId; route the
  //       // provider's UUID into both fields so a single endpoint can
  //       // find the email regardless of which side it indexes.
  //       patientId: pr.value,
  //       providerId: pr.value,
  //       appointmentUrl: openUrl,
  //       xTENANTID,
  //     })
  //   );
  // }
  //   if (tasks.length === 0) return;
  //   const results = await Promise.allSettled(tasks);
  //   const failures = results.filter((r) => r.status === "rejected");
  //   if (failures.length === 0) {
  //     showSnackbar({ type: "success", message: "Invite links sent" });
  //   } else {
  //     const firstErr = (failures[0] as PromiseRejectedResult).reason;
  //     showSnackbar(getErrorSnackbar(firstErr));
  //   }
  //   },
  //   [
  //     params.room,
  //     params.appointmentId,
  //     params.patientId,
  //     params.startTime,
  //     patientName,
  //     // inviteAppointment,
  //     showSnackbar,
  //   ]
  // );

  // Leave on unmount.
  useEffect(() => {
    return () => {
      session.leave();
    };
  }, []);

  // Declined guests re-enter from scratch: the host's deny removed us from
  // the session, so a plain knock has nothing to travel over. rejoin() puts
  // us back in the lobby, where the knock loop raises a fresh admit request.
  const handleAskToJoinAgain = useCallback(() => {
    void session.rejoin();
  }, [session.rejoin]);

  const handleLeave = async () => {
    // Host clicking Leave should end the meeting for everyone. Broadcast
    // first so guests disconnect immediately, then tear down our own
    // session. Keep the host's flow snappy — don't await the broadcast
    // beyond its initial send.
    if (session.isHost) {
      try {
        await lobby.announceHostLeft();
      } catch {
        /* ignore — guests still have the user-removed backstop */
      }
    }
    await session.leave();
    onExit?.();
  };

  const handleJoinClick = async () => {
    // Don't auto-enable the camera on join. Host should land with video
    // off and decide when to turn it on; guests pick their preference in
    // the lobby and the videoOn state in useTelehealthSession persists
    // through the lobby→active transition, so whatever they had set in
    // the waiting room follows them in.
    await session.join();
  };

  // Rejoin entry point (host link or any URL with `skipPreflight=1`):
  // bypass the "Ready to join?" screen and join immediately. Tied to the
  // initial idle phase so manual leaves/disconnects don't loop us back in.
  // Use session.rejoin (not plain join) so we get the explicit leave()
  // + 400ms settle window the SDK needs to recover from a page refresh —
  // the previous tab's pagehide leave hasn't fully propagated server-side
  // yet, and a fresh join() landing in that window races against the
  // stale session and produces an empty meeting (no self, no peers)
  // until the user manually clicks Rejoin.
  const autoJoinedRef = useRef(false);
  useEffect(() => {
    if (autoJoinedRef.current) return;
    if (!params.skipPreflight) return;
    if (session.phase !== "idle") return;
    // Wait for ZoomClientProvider's async init. If we fired now with a
    // null zmClient, doJoin would set a "Zoom client not initialized"
    // error and stop — and because we'd already have flipped
    // autoJoinedRef, no further state change would re-trigger this
    // effect to retry. Gating on zmClient lets the effect re-run the
    // moment the context hydrates.
    if (!zmClient) return;
    autoJoinedRef.current = true;
    session.rejoin();
  }, [params.skipPreflight, session.phase, zmClient]);

  // Stage roster filter — unified across host and admitted guests. The
  // allowlist is built from `lobby.admittedIds` (host derives this locally
  // from admit clicks; guests receive it via the host's "roster" broadcast)
  // plus the local user's selfId (always visible to themselves) plus any
  // user the SDK marks as host. This closes the window where an unadmitted
  // guest would otherwise flash into the video grid for both the host and
  // for other already-admitted guests.
  const inMeetingParticipants = useMemo(() => {
    const allowed = new Set<number>(lobby.admittedIds);
    if (session.selfId !== undefined) allowed.add(session.selfId);
    let filtered = session.participants.filter(
      (p) => allowed.has(p.userId) || !!p.isHost
    );
    // Always project our local React state (videoOn/audio*) onto the self
    // tile. The SDK's `getAllUser()` doesn't always update bVideoOn for
    // the local user the moment we call stopVideo()/startVideo(); without
    // this override the canvas keeps its last frame while VideoStage
    // still treats showMedia as true, producing a frozen "black screen"
    // after toggling the camera off.
    if (session.selfId !== undefined) {
      filtered = filtered.map((p) =>
        p.userId === session.selfId
          ? {
              ...p,
              bVideoOn: session.videoOn,
              muted: !session.audioStarted || session.audioMuted,
            }
          : p
      );
      // Defensive fallback: Zoom's getAllUser() can take a beat after
      // join() to include the local user. Synthesize a placeholder so
      // the host always sees themselves; once the SDK populates, the
      // override above keeps state consistent.
      if (!filtered.some((p) => p.userId === session.selfId)) {
        filtered.push({
          userId: session.selfId,
          displayName: params.displayName,
          bVideoOn: session.videoOn,
          muted: !session.audioStarted || session.audioMuted,
          isHost: session.isHost,
        } as any);
      }
    }
    return filtered;
  }, [
    session.participants,
    session.selfId,
    session.videoOn,
    session.audioStarted,
    session.audioMuted,
    session.isHost,
    params.displayName,
    lobby.admittedIds,
  ]);

  const anySharing =
    share.isSelfSharing || share.activeRemoteShareUserId !== undefined;

  const showPreflight =
    session.phase === "idle" ||
    session.phase === "joining" ||
    (session.phase === "disconnected" && session.attempts === 0);

  if (showPreflight) {
    // ZoomClientProvider hydrates `zmClient` asynchronously. Until it does,
    // there is nothing to join: the skipPreflight auto-join effect is parked
    // on the same condition, and a manual Join would only raise NO_CLIENT.
    if (!zmClient) {
      return (
        <InitializingScreen
          message={ZOOM_CLIENT_INIT_MESSAGE}
          onCancel={onExit}
        />
      );
    }

    return (
      <PreflightScreen
        displayName={params.displayName}
        joining={session.phase === "joining"}
        errorMessage={session.error?.message}
        onJoin={handleJoinClick}
        onCancel={() => onExit?.()}
      />
    );
  }

  return (
    <Stack
      sx={{
        position: "relative",
        flex: 1,
        // The wrapper backdrop shows around video tiles in the active phase.
        // Lobby/preflight cover it with their own themed surface, so this
        // dark color only ever sits behind video — where it should be dark.
        bgcolor: VIDEO_BG,
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ConnectionStatus phase={session.phase} />

      {session.phase === "lobby" || session.phase === "denied" ? (
        <Lobby
          displayName={params.displayName}
          status={session.phase === "denied" ? "denied" : "waiting"}
          onLeave={handleLeave}
          onRejoin={handleAskToJoinAgain}
          micActive={session.audioStarted && !session.audioMuted}
          videoOn={session.videoOn}
          onToggleAudio={session.toggleAudio}
          onToggleVideo={session.toggleVideo}
          videoPreview={
            session.selfId !== undefined ? (
              <VideoStage
                participants={session.participants.filter(
                  (p) => p.userId === session.selfId
                )}
                selfId={session.selfId}
                layout="grid"
              />
            ) : undefined
          }
          roomMembers={session.participants
            .filter((p) => p.userId !== session.selfId)
            // Only count people the host has admitted (or is themselves a
            // host) — other lobby waiters aren't actually "in" the visit.
            // For guests, lobby.admittedIds is delivered via the host's
            // roster broadcast.
            .filter((p) => lobby.admittedIds.includes(p.userId) || !!p.isHost)
            .map((p) => p.displayName as string)
            .filter(Boolean)}
        />
      ) : (
        <>
          {session.isHost &&
            !isMiniMode &&
            session.phase === "active" &&
            onCancelAppointment &&
            onNoShow &&
            onStartEncounter && (
              <HostActionBar
                patientName={patientName}
                providerName={providerName}
                startTime={params.startTime}
                onCancel={onCancelAppointment}
                onNoShow={onNoShow}
                onStartEncounter={onStartEncounter}
              />
            )}
          <Stack
            direction="row"
            sx={{
              flex: 1,
              overflow: "hidden",

              // Defensive: prevent the row children wrapping onto a second
              // line on narrow viewports — that's what was making the
              // participant see their video sidebar below the share area
              // instead of beside it.
              flexWrap: "nowrap",

              minHeight: 0,
              width: "100%",
            }}
          >
            {/* ScreenShareView is mounted ONCE here regardless of sharing
              state — its width just collapses to 0 when nobody is sharing.
              Re-mounting would detach the SDK from the <video> ref it had
              just bound to during startShareScreen(). */}
            <Box
              sx={{
                flex: anySharing ? "1 1 auto" : "0 0 0px",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "stretch",
              }}
            >
              <ScreenShareView
                isSelfSharing={share.isSelfSharing}
                activeRemoteShareUserId={share.activeRemoteShareUserId}
                selfVideoRef={share.selfVideoRef}
                remoteCanvasRef={share.remoteCanvasRef}
              />
            </Box>
            <Box
              sx={{
                flex: anySharing ? "0 0 240px" : "1 1 auto",
                minWidth: 0,
                minHeight: 0,
                borderLeft: anySharing ? 1 : 0,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <VideoStage
                participants={inMeetingParticipants}
                selfId={session.selfId}
                speakingIds={speakingIds}
                layout={
                  anySharing
                    ? "sidebar"
                    : inMeetingParticipants.length === 2
                      ? "two"
                      : "grid"
                }
              />
            </Box>
            {!isMiniMode && (
              <ParticipantsPanel
                open={panelOpen}
                isHost={session.isHost}
                selfId={session.selfId}
                participants={inMeetingParticipants}
                knocks={lobby.knocks}
                onAdmit={lobby.admit}
                onDeny={lobby.deny}
                onKick={(uid) => {
                  lobby.kick(uid);
                  host.remove(uid);
                }}
                onClose={() => setPanelOpen(false)}
                speakingIds={speakingIds}
                onMuteParticipant={
                  session.isHost ? host.muteParticipant : undefined
                }
                onAskToUnmute={session.isHost ? host.askToUnmute : undefined}
                // onInvite={() => session.isHost ? handleInvite : undefined}
              />
            )}
          </Stack>
        </>
      )}

      {session.phase !== "lobby" && session.phase !== "denied" && (
        <ControlBar
          audioMuted={session.audioMuted}
          audioStarted={session.audioStarted}
          videoOn={session.videoOn}
          isSharing={share.isSelfSharing}
          isHost={session.isHost}
          pendingKnocks={lobby.knocks.length}
          onCopyLink={buildInviteUrl}
          onToggleAudio={session.toggleAudio}
          onToggleVideo={session.toggleVideo}
          onToggleShare={
            share.isSelfSharing ? share.stopShare : share.startShare
          }
          onLeave={handleLeave}
          onRejoin={session.rejoin}
          onTogglePanel={isMiniMode ? undefined : () => setPanelOpen((v) => !v)}
          onMuteAll={session.isHost && !isMiniMode ? host.muteAll : undefined}
          onPip={isMiniMode ? undefined : onEnterMiniMode}
        />
      )}

      {session.isHost && session.phase === "active" && (
        <AdmitKnockModal
          knock={lobby.knocks[0]}
          remaining={Math.max(lobby.knocks.length - 1, 0)}
          onAdmit={() => {
            const k = lobby.knocks[0];
            if (k) lobby.admit(k.userId);
          }}
          onDeny={() => {
            const k = lobby.knocks[0];
            if (k) lobby.deny(k.userId);
          }}
        />
      )}

      <RejoinPrompt
        open={session.phase === "disconnected" && session.attempts >= 1}
        attempts={session.attempts}
        message={session.error?.message}
        onRejoin={session.rejoin}
        onClose={handleLeave}
      />
    </Stack>
  );
}
